import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LoaderCircleIcon,
  PrinterIcon,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { AsortiGrid } from '@app/components/asorti-grid'
import { CariLink, StockLink } from '@app/components/entity-link'
import { ProductImage } from '@app/components/product-image'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'
import { PrintPortal, printedOn, usePrintJob } from '@app/lib/print-portal'
import { ordersApi, ordersQueryKeys } from '@app/features/orders/api'
import { formatAmount, formatDate, formatDateTime, formatQty, percentText } from '@app/features/orders/format'
import type {
  OrderColorBreakdown,
  OrderDetailResponse,
  OrderLine,
  OrderSizeCell,
} from '@app/features/orders/types'
import {
  toAsortiCells,
  EmptyState,
  StatusBadge,
  ErrorState,
  ShipStatusBadge,
  ShipBar,
  TableSkeleton,
  DueDateCell,
  TypeBadge,
} from '@app/features/orders/ui'
import '@app/styles/print-docs.css'

/**
 * Order detail — customer, dates, lines, and per-line asorti breakdown.
 *
 * Korgun's order screen tucks the size breakdown away in a separate
 * sub-window; here every line's size strip stays inline, and the color
 * breakdown expands with a click.
 */
export function OrderDetailScreen() {
  const { sipNo } = useParams({ from: '/_korumali/siparisler/$sipNo' })
  const navigate = useNavigate()

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ordersQueryKeys.detail(sipNo),
    queryFn: () => ordersApi.detail(sipNo),
  })

  /*
   * ── Purchase-order bridge ────────────────────────────────────────────────
   *
   * `Siparis_Kay` holds sales and purchase orders in one table, so a number
   * that belongs to a PO (`SipTip = 'A'`) resolves perfectly well here — and
   * would render the wrong screen for it: this view reads the Si_GCHar ledger
   * as "ordered / shipped", while on a purchase order that ledger is INVERTED
   * (Cikan = ordered, Giren = received). Every quantity would be backwards.
   *
   * /satinalma/<n> is the canonical purchase view and stays that way — it does
   * NOT offer a way back into this screen. The bridge is one-directional: a PO
   * number reached from anywhere (a typed URL, an old bookmark, a cari ledger
   * row) lands on the right screen instead. `replace` keeps the browser's Back
   * button pointing at wherever the user came from rather than bouncing them
   * through this page again.
   */
  const isPurchaseOrder = data?.header.type === 'A'
  useEffect(() => {
    if (!isPurchaseOrder) return
    void navigate({ to: '/satinalma/$sipNo', params: { sipNo }, replace: true })
  }, [isPurchaseOrder, navigate, sipNo])

  // 404 is the server's final answer — retrying cannot conjure the record up,
  // so the only useful action is the way back to the list.
  const notFound = error instanceof ApiError && error.status === 404

  /*
   * So is 403: this number belongs to a purchase order and the account may not
   * open satınalma (orders.ts refuses a PO's detail — see auth.contract.ts).
   * The bridge above never fires for them, since the payload that would say
   * "this is a PO" is exactly what was refused, so this branch is the whole
   * explanation they get. It carries no retry button for the same reason as the
   * 404: the answer will not change.
   */
  const forbidden = error instanceof ApiError && error.status === 403

  return (
    <>
      <div className="mb-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/siparisler">
            <ArrowLeftIcon aria-hidden />
            Siparişler
          </Link>
        </Button>
      </div>

      {isPending ? (
        <DetailSkeleton />
      ) : isError ? (
        <Card>
          {notFound ? (
            <ErrorState
              title="Sipariş bulunamadı"
              message={`${sipNo} numaralı sipariş Korgun'da kayıtlı değil. Listeden doğru siparişi seçebilirsiniz.`}
              action={
                <Button asChild variant="primary">
                  <Link to="/siparisler">Sipariş listesine dön</Link>
                </Button>
              }
            />
          ) : forbidden ? (
            <ErrorState
              title="Bu sipariş size kapalı"
              message={`${sipNo} numaralı kayıt bir alış siparişi; satınalma kayıtlarını görme yetkiniz yok.`}
              action={
                <Button asChild variant="primary">
                  <Link to="/siparisler">Sipariş listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              message={error instanceof Error ? error.message : 'Sipariş getirilemedi.'}
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      ) : isPurchaseOrder ? (
        <PurchaseRedirectNotice sipNo={sipNo} />
      ) : (
        <DetailContent data={data} />
      )}
    </>
  )
}

/** Shown for the single frame between "this is a PO" and the navigation landing. */
function PurchaseRedirectNotice({ sipNo }: { sipNo: string }) {
  return (
    <Card>
      <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
        <LoaderCircleIcon className="size-5 animate-spin text-ink-muted" aria-hidden />
        <p className="font-display text-[0.9375rem] font-semibold text-ink">
          Bu bir alış siparişi
        </p>
        <p className="max-w-sm text-data text-ink-muted">
          Satınalma görünümüne yönlendiriliyorsunuz.
        </p>
        <Button asChild variant="outline" className="mt-1">
          <Link to="/satinalma/$sipNo" params={{ sipNo }} replace>
            Satınalma görünümünü aç
          </Link>
        </Button>
      </div>
    </Card>
  )
}

function DetailContent({ data }: { data: OrderDetailResponse }) {
  const { header, summary, currencyTotals, lines } = data
  const [openLines, setOpenLines] = useState<ReadonlySet<number>>(new Set())

  /*
   * The sipariş formu is built from the response already on screen, so `load`
   * only takes a snapshot of it — see the same call in the satınalma screen.
   */
  const print = usePrintJob<OrderDetailResponse>({
    bodyClass: PRINT_DOCUMENT_CLASS,
    resetKey: String(header.sipNo),
    load: async () => data,
    canPrint: (snapshot) => snapshot.lines.length > 0,
  })

  // Only the lines whose colour breakdown says something the row doesn't
  // already show can be opened — see `hasColorDetail`.
  const expandable = lines.filter(hasColorDetail)
  const allOpen = expandable.length > 0 && openLines.size === expandable.length

  function toggleLine(inx: number) {
    setOpenLines((prev) => {
      const next = new Set(prev)
      if (next.has(inx)) next.delete(inx)
      else next.add(inx)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Header card ──────────────────────────────────────────────────── */}
      <Card>
        <div className="flex flex-col gap-4 px-3.5 py-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="eyebrow">
              Sipariş <span className="font-mono">{header.sipNo}</span>
              {header.documentNo ? (
                <>
                  {' · Belge '}
                  <span className="font-mono">{header.documentNo}</span>
                </>
              ) : null}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-semibold tracking-tight text-ink">
              <CariLink
                ckod={header.cariKod}
                title={`${header.cariName} cari kartını aç`}
                className="text-ink hover:text-kordovan hover:no-underline"
                // No hover accent when it is not a link (no cari access).
                plainClassName="text-ink"
              >
                {header.cariName}
              </CariLink>
            </h2>
            <p className="mt-0.5 text-data text-ink-muted">
              <CariLink ckod={header.cariKod} className="font-mono" />
              {header.cariTitle && header.cariTitle !== header.cariName ? ` · ${header.cariTitle}` : ''}
              {header.cariCountry ? ` · ${header.cariCountry}` : ''}
              {header.cariOverseas === 'yd' ? ' · yurt dışı' : ''}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <TypeBadge type={header.type} typeName={header.typeName} />
              <StatusBadge status={header.status} />
              <ShipStatusBadge status={summary.shipStatus} overdue={summary.overdue} />
              {summary.overdue && summary.overdueDays !== null ? (
                <Badge tone="warn">Termin {formatQty(summary.overdueDays)} gün geçti</Badge>
              ) : null}
              {summary.mixedCurrency ? <Badge tone="warn">Karışık para birimi</Badge> : null}
            </div>

            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={print.run}
                disabled={lines.length === 0}
                title={
                  lines.length === 0
                    ? 'Satırı olmayan sipariş yazdırılamaz'
                    : 'Sipariş formunu A4 belge olarak yazdır'
                }
              >
                <PrinterIcon aria-hidden />
                Yazdır
              </Button>
            </div>
          </div>

          {/* Shipment summary — the most-checked number on the page. */}
          <div className="shrink-0 rounded-sm border border-hairline bg-kagit px-3 py-2.5 lg:w-72">
            <div className="flex items-baseline justify-between gap-3">
              <span className="eyebrow">Sevkiyat</span>
              <span
                className={cn(
                  'font-mono text-xl tnum font-semibold',
                  summary.shipStatus === 'complete'
                    ? 'text-zeytin'
                    : summary.overdue
                      ? 'text-hardal'
                      : 'text-ink',
                )}
              >
                {percentText(summary.percent)}
              </span>
            </div>
            <ShipBar
              percent={summary.percent}
              status={summary.shipStatus}
              overdue={summary.overdue}
              showLabel={false}
              className="mt-1.5"
            />
            <dl className="mt-2.5 grid grid-cols-3 gap-2 text-center">
              <MiniStat label="Sipariş" value={formatQty(summary.orderedQty)} />
              <MiniStat label="Sevk" value={formatQty(summary.shippedQty)} />
              <MiniStat
                label="Kalan"
                value={formatQty(summary.kalanQty)}
                highlighted={summary.kalanQty > 0}
              />
            </dl>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-hairline px-3.5 py-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="Sipariş tarihi" value={formatDate(header.orderDate)} mono />
          <Field
            label="Termin"
            value={
              <DueDateCell dateText={formatDate(header.deliveryDate)} overdueDays={summary.overdueDays} />
            }
          />
          <Field label="Sevk tarihi" value={formatDate(header.shipDate)} mono />
          <Field label="Üretime giriş" value={formatDate(header.productionEntryDate)} mono />
          <Field label="Lokasyon" value={header.locationName ?? header.location ?? '—'} />
          <Field label="Siparişi alan" value={header.takenBy ?? '—'} />
          <Field label="Ödeme şekli" value={header.paymentMethod ?? '—'} />
          <Field
            label="İskonto"
            value={header.discountRate > 0 ? `%${header.discountRate}` : '—'}
            mono
          />
          <Field label="Satır sayısı" value={formatQty(summary.lineCount)} mono />
          <Field label="Oluşturan" value={header.createdBy ?? '—'} />
          <Field label="Oluşturma" value={formatDateTime(header.createdAt)} mono />
          <Field label="Son güncelleme" value={formatDateTime(header.updatedAt)} mono />
        </dl>

        {header.notes || header.closingNote ? (
          <div className="border-t border-hairline px-3.5 py-3">
            {header.notes ? (
              <>
                <p className="eyebrow">Sipariş notu</p>
                <p className="mt-1 whitespace-pre-line text-data text-ink">{header.notes}</p>
              </>
            ) : null}
            {header.closingNote ? (
              <>
                <p className={cn('eyebrow', header.notes && 'mt-3')}>Kapanış notu</p>
                <p className="mt-1 whitespace-pre-line text-data text-ink">{header.closingNote}</p>
              </>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* ── Lines ────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Sipariş satırları</CardTitle>
          {expandable.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setOpenLines(allOpen ? new Set() : new Set(expandable.map((s) => s.sipHarInx)))
              }
            >
              {allOpen ? 'Renk kırılımlarını kapat' : 'Renk kırılımlarını aç'}
            </Button>
          ) : null}
        </CardHeader>

        {lines.length === 0 ? (
          <EmptyState
            title="Bu siparişte satır yok"
            description="Korgun'da başlık açılmış ama satır girilmemiş. Sipariş listesine dönüp başka bir kayda bakabilirsiniz."
            action={
              <Button asChild variant="primary">
                <Link to="/siparisler">Sipariş listesine dön</Link>
              </Button>
            }
          />
        ) : (
          <TableScroller>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="w-10 text-right">#</TableHead>
                  <TableHead className="min-w-52">Ürün</TableHead>
                  <TableHead className="min-w-40">Asorti / beden dağılımı</TableHead>
                  <TableHead className="w-20 text-right">Sipariş</TableHead>
                  <TableHead className="hidden w-20 text-right lg:table-cell">Sevk</TableHead>
                  <TableHead className="w-20 text-right">Kalan</TableHead>
                  <TableHead className="w-32">Sevkiyat</TableHead>
                  <TableHead className="hidden w-24 text-right md:table-cell">Fiyat</TableHead>
                  <TableHead className="w-28 text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <LineGroup
                    key={line.sipHarInx}
                    line={line}
                    open={openLines.has(line.sipHarInx)}
                    onToggle={() => toggleLine(line.sipHarInx)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableScroller>
        )}

        {/* ── Totals ─────────────────────────────────────────────────────── */}
        {currencyTotals.length > 0 ? (
          <div className="flex flex-col items-end gap-1 border-t border-hairline px-3.5 py-2.5">
            {currencyTotals.map((total) => {
              const mainCurrency = total.currency === summary.currency
              return (
                <div
                  key={total.currency}
                  className={cn(
                    'flex w-full max-w-sm items-baseline justify-between gap-4',
                    mainCurrency ? 'text-ink' : 'text-ink-muted',
                  )}
                >
                  <span className={cn('text-data', mainCurrency && 'font-medium')}>
                    {mainCurrency
                      ? `Sipariş toplamı (${total.currency})`
                      : `${total.currency} karşılığı`}
                  </span>
                  <span
                    className={cn(
                      'font-mono tnum tabular-nums',
                      mainCurrency ? 'text-[0.9375rem] font-semibold' : 'text-data',
                    )}
                  >
                    {formatAmount(total.netAmount, total.currency)}
                  </span>
                </div>
              )
            })}
          </div>
        ) : null}
      </Card>

      {/* Portalled straight onto <body>, outside #root — see lib/print-portal.tsx
          and styles/print-docs.css. Invisible on screen either way. */}
      {print.status === 'ready' ? <OrderFormDocument data={print.data} /> : null}
    </div>
  )
}

/* ── Printable sipariş formu ──────────────────────────────────────────────── */

/** Shared by every document in `styles/print-docs.css` — see purchase-detail. */
const PRINT_DOCUMENT_CLASS = 'print-document'

/**
 * The A4 order form — what goes back to the customer, and what production
 * works from until the order is released.
 *
 * Termin is the one figure the whole sheet turns on, so it is boxed in the
 * terms grid rather than merely bold: this page is read across a workbench,
 * and a due date that gets skimmed past is the exact failure the form exists
 * to prevent.
 *
 * Sizes print as `37:12 38:24` pairs per colourway instead of the on-screen
 * asorti grid. The grid is the better instrument on a screen that can scroll;
 * on paper it would eat a third of the width, and the pairs are the notation
 * the shop floor writes by hand anyway.
 */
function OrderFormDocument({ data }: { data: OrderDetailResponse }) {
  const { header, summary, currencyTotals, lines } = data
  const priced = lines.some((line) => line.price > 0 || (line.netAmount ?? 0) !== 0)
  const pricedTotals = currencyTotals.filter((total) => total.netAmount !== 0)
  const cityLine = [header.cariCity, header.cariCountry].filter(Boolean).join(' · ')

  const meta: { label: string; value: string; emphasis?: boolean }[] = [
    { label: 'Sipariş tarihi', value: formatDate(header.orderDate) },
    {
      label: 'Termin',
      value: formatDate(header.deliveryDate),
      emphasis: header.deliveryDate !== null,
    },
    { label: 'Sevk tarihi', value: formatDate(header.shipDate) },
    { label: 'Lokasyon', value: header.locationName ?? header.location ?? '—' },
    { label: 'Siparişi alan', value: header.takenBy ?? '—' },
    { label: 'Ödeme şekli', value: header.paymentMethod ?? '—' },
    { label: 'Satır sayısı', value: formatQty(summary.lineCount) },
    { label: 'Toplam adet', value: formatQty(summary.orderedQty) },
  ]
  if (header.documentNo) meta.push({ label: 'Belge no', value: header.documentNo })

  return (
    <PrintPortal className="print-doc">
      <header className="doc-head">
        <div>
          <p className="wordmark">Modelle</p>
          <p className="wordmark-sub">Ayakkabı fabrikası · Sipariş</p>
        </div>
        <div className="doc-id">
          {/* A numune (SipTip 'ST') is never shipped and never produced — the
              document has to say so on its face, not only in the system. */}
          <p className="doc-kind">
            Sipariş Formu{header.type === 'ST' ? ' · Numune' : ''}
          </p>
          <p className="doc-no">{header.sipNo}</p>
          <p className="doc-date">{formatDate(header.orderDate)}</p>
        </div>
      </header>

      <section className="parties">
        <div className="party">
          <p className="doc-label">Müşteri</p>
          <p className="party-name">{header.cariName}</p>
          <p className="party-line mono">{header.cariKod}</p>
          {header.cariTitle && header.cariTitle !== header.cariName ? (
            <p className="party-line">{header.cariTitle}</p>
          ) : null}
          {cityLine ? <p className="party-line">{cityLine}</p> : null}
          {header.cariPhone ? (
            <p className="party-line">
              Tel <span className="mono">{header.cariPhone}</span>
            </p>
          ) : null}
        </div>

        <dl className="meta">
          {meta.map((field) => (
            <div key={field.label} className={field.emphasis ? 'meta-key' : undefined}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <table>
        <thead>
          <tr>
            <th className="col-no num">#</th>
            <th>Ürün ve beden dağılımı</th>
            <th className="col-qty num">Adet</th>
            {priced ? <th className="col-price num">Birim fiyat</th> : null}
            {priced ? <th className="col-amount num">Tutar</th> : null}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.sipHarInx}>
              <td className="num">{line.lineNo}</td>
              <td>
                <span className="line-name">{line.stockName ?? line.skod ?? '—'}</span>
                {line.skod ? <span className="line-code">{line.skod}</span> : null}
                <AsortiRuns line={line} />
                {line.lineNote ? <span className="line-note">{line.lineNote}</span> : null}
              </td>
              <td className="num">{formatQty(line.orderedQty || line.quantity)}</td>
              {priced ? (
                <td className="num">
                  {line.price > 0 ? formatAmount(line.price, line.currency) : '—'}
                </td>
              ) : null}
              {priced ? (
                <td className="num">
                  {line.netAmount === null || line.netAmount === 0
                    ? '—'
                    : formatAmount(line.netAmount, line.currency)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      {pricedTotals.length > 0 ? (
        <div className="totals">
          {pricedTotals.map((total) => {
            const main = total.currency === summary.currency
            return (
              <div key={total.currency} className={cn('total-row', main && 'total-main')}>
                <span>
                  {main ? `Sipariş toplamı (${total.currency})` : `${total.currency} karşılığı`}
                </span>
                <span className="total-value">{formatAmount(total.netAmount, total.currency)}</span>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="totals-note">Bu siparişte fiyat girilmemiştir.</p>
      )}

      {header.notes || header.closingNote ? (
        <section className="notes">
          {header.notes ? (
            <>
              <p className="doc-label">Sipariş notu</p>
              <p className="note-body">{header.notes}</p>
            </>
          ) : null}
          {header.closingNote ? (
            <>
              <p className="doc-label">Kapanış notu</p>
              <p className="note-body">{header.closingNote}</p>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="signatures">
        <div className="sign">
          <div className="sign-rule" />
          <p className="doc-label">Hazırlayan</p>
        </div>
        <div className="sign">
          <div className="sign-rule" />
          <p className="doc-label">Müşteri onayı</p>
        </div>
      </section>

      <footer className="doc-foot">
        <span>Panel — {printedOn()}</span>
        <span className="mono">Sipariş Formu {header.sipNo}</span>
      </footer>
    </PrintPortal>
  )
}

/**
 * One compact run per colourway: `SİYAH 37:2 38:3 39:2 = 7 · A37-41 ×3`.
 *
 * The colour label is dropped when there is no colour on the line (`RKod 0`),
 * the asorti code is appended only where one is defined, and a line whose
 * ledger carries no size steps at all falls back to the line's own strip — so
 * every line says as much as it can and no more.
 */
function AsortiRuns({ line }: { line: OrderLine }) {
  const groups = line.colors.length > 0 ? line.colors : null

  if (groups === null) {
    const run = sizeRun(line.sizes)
    return run ? <span className="line-sizes">{run}</span> : null
  }

  return (
    <span className="line-sizes">
      {groups.map((color) => {
        const run = sizeRun(color.sizes)
        return (
          <span key={color.rkod} className="size-group">
            {isColorless(color) ? null : (
              <span className="size-group-label">{color.colorName} </span>
            )}
            {run ? `${run} = ` : ''}
            {formatQty(color.orderedQty)}
            {color.asortiKod
              ? ` · ${color.asortiKod}${color.asortiQty ? ` ×${formatQty(color.asortiQty)}` : ''}`
              : ''}
          </span>
        )
      })}
    </span>
  )
}

/** `37:12 38:24` — null when the line carries no per-size breakdown. */
function sizeRun(sizes: readonly OrderSizeCell[]): string | null {
  const pairs = sizes
    .filter((cell) => cell.ordered > 0)
    .map((cell) => `${cell.size}:${formatQty(cell.ordered)}`)
  return pairs.length > 0 ? pairs.join(' ') : null
}

/* ── Line + expandable color breakdown ───────────────────────────────────────── */

/**
 * `RKod = 0` is Korgun's "no colour entered" — and because the API falls back
 * to `Renk <RKod>` when `P_RnkTip` has no matching row, an unset colour reads
 * as the nonsense literal "Renk 0" on screen. Say what it actually means.
 */
function isColorless(color: OrderColorBreakdown): boolean {
  return color.rkod === 0 || color.colorName.trim() === ''
}

/**
 * Is the colour breakdown worth a disclosure triangle?
 *
 * The row already prints the line's size strip and its ordered/shipped/kalan
 * numbers, so a lone colourway with no colour and no asorti carton adds
 * nothing — opening it just repeats the row one indent to the right. More than
 * one colourway (which is how the quantities actually split), a named colour or
 * an asorti code all earn the click.
 */
function hasColorDetail(line: OrderLine): boolean {
  if (line.colors.length > 1) return true
  const only = line.colors[0]
  if (!only) return false
  return !isColorless(only) || only.asortiKod !== null
}

function LineGroup({
  line,
  open,
  onToggle,
}: {
  line: OrderLine
  open: boolean
  onToggle: () => void
}) {
  const colorCount = line.colors.length
  const expandable = hasColorDetail(line)

  return (
    <>
      <TableRow className={cn(open && 'bg-kordovan/4')}>
        <TableCell>
          {expandable ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              aria-label={`${line.skod ?? 'Satır'} renk kırılımını ${open ? 'kapat' : 'aç'}`}
              className="grid size-5 place-items-center rounded-xs text-ink-muted transition-colors hover:bg-kordovan/8 hover:text-kordovan"
            >
              {open ? (
                <ChevronDownIcon className="size-3.5" aria-hidden />
              ) : (
                <ChevronRightIcon className="size-3.5" aria-hidden />
              )}
            </button>
          ) : null}
        </TableCell>

        <TableNumCell className="text-ink-muted">{line.lineNo}</TableNumCell>

        <TableCell>
          {/* The product's card photo. A line can carry several colourways, so
              this is the model — the colour's own picture is one row down, in
              the breakdown. */}
          <span className="flex items-start gap-2">
            <ProductImage
              imageKey={line.imageKey}
              alt={`${line.stockName ?? line.skod ?? 'Ürün'} fotoğrafı`}
              size="line"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-ink">
                {line.stockName ?? line.skod ?? '—'}
              </span>
              <span className="block truncate font-mono text-micro text-ink-muted">
                {line.skod ? <StockLink skod={line.skod} /> : '—'}
                {line.position ? ` · konum ${line.position}` : ''}
                {line.lotNo ? ` · lot ${line.lotNo}` : ''}
                {colorCount > 1 ? ` · ${colorCount} renk` : ''}
              </span>
              {line.lineNote ? (
                <span
                  className="mt-0.5 block truncate text-micro text-ink-muted/80"
                  title={line.lineNote}
                >
                  {line.lineNote}
                </span>
              ) : null}
            </span>
          </span>
        </TableCell>

        <TableCell>
          {line.sizes.length > 0 ? (
            <AsortiGrid cells={toAsortiCells(line.sizes)} total density="sm" />
          ) : (
            <span className="text-micro text-ink-muted/70">Beden kırılımı yok</span>
          )}
        </TableCell>

        <TableNumCell>{formatQty(line.orderedQty || line.quantity)}</TableNumCell>
        <TableNumCell className="hidden text-ink-muted lg:table-cell">
          {formatQty(line.shippedQty)}
        </TableNumCell>
        <TableNumCell className={cn(line.kalanQty > 0 ? 'font-medium text-ink' : 'text-ink-muted/60')}>
          {line.kalanQty > 0 ? formatQty(line.kalanQty) : '—'}
        </TableNumCell>

        <TableCell>
          <div className="flex flex-col gap-1">
            <ShipBar percent={line.percent} status={line.shipStatus} showLabel={false} />
            <ShipStatusBadge status={line.shipStatus} />
          </div>
        </TableCell>

        <TableNumCell className="hidden text-ink-muted md:table-cell">
          {line.price > 0 ? formatAmount(line.price, line.currency) : '—'}
        </TableNumCell>

        <TableNumCell className="font-medium">
          {line.netAmount === null || line.netAmount === 0
            ? '—'
            : formatAmount(line.netAmount, line.currency)}
        </TableNumCell>
      </TableRow>

      {open && expandable ? (
        <TableRow className="bg-kagit hover:bg-kagit">
          <TableCell colSpan={10} className="h-auto py-3 pl-10">
            <p className="eyebrow mb-2">Renk ve asorti kırılımı</p>
            <div className="flex flex-wrap gap-4">
              {line.colors.map((color) => (
                <div key={color.rkod} className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {/* This colourway's own photo (S_DetPicture on the line's
                        SKod + this RKod) — the reason to open the breakdown. */}
                    <ProductImage
                      imageKey={color.imageKey ?? line.imageKey}
                      alt={`${line.stockName ?? line.skod ?? 'Ürün'} · ${
                        isColorless(color) ? `renk ${color.rkod}` : color.colorName
                      } fotoğrafı`}
                      size="line"
                    />
                    {isColorless(color) ? (
                      <span className="text-data text-ink-muted">Renk belirtilmemiş</span>
                    ) : (
                      <>
                        <span className="text-data font-medium text-ink">{color.colorName}</span>
                        <span className="font-mono text-micro text-ink-muted">RKod {color.rkod}</span>
                      </>
                    )}
                    {color.asortiKod ? (
                      <span
                        className="font-mono text-micro text-celik"
                        title={color.asortiLabel ?? undefined}
                      >
                        {color.asortiKod}
                        {color.asortiQty ? ` × ${formatQty(color.asortiQty)}` : ''}
                      </span>
                    ) : null}
                  </div>
                  <AsortiGrid cells={toAsortiCells(color.sizes)} total={color.orderedQty} />
                  <p className="mt-1 font-mono text-micro tnum text-ink-muted">
                    sevk {formatQty(color.shippedQty)} · kalan {formatQty(color.kalanQty)}
                    {color.asortiLabel ? ` · ${color.asortiLabel}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}

/* ── Small parts ──────────────────────────────────────────────────────────────── */

function Field({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow">{label}</dt>
      <dd className={cn('mt-0.5 truncate text-data text-ink', mono && 'font-mono tnum')}>{value}</dd>
    </div>
  )
}

function MiniStat({
  label,
  value,
  highlighted = false,
}: {
  label: string
  value: string
  highlighted?: boolean
}) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 font-mono text-data tnum',
          highlighted ? 'font-semibold text-ink' : 'text-ink-muted',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Card>
        <div className="px-3.5 py-4">
          <span className="block h-2.5 w-24 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-6 w-64 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-2.5 w-44 animate-pulse rounded-xs bg-hairline" />
        </div>
      </Card>
      <Card>
        <TableScroller>
          <Table>
            <TableBody>
              <TableSkeleton rows={6} cols={7} />
            </TableBody>
          </Table>
        </TableScroller>
      </Card>
    </div>
  )
}
